import type { Dispatch, SetStateAction } from "react"
import { useCallback, useEffect, useRef } from "react"
import { useFormikContext } from "formik"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useEnvironment } from "../../../../../modules/environment/envHooks"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import {
    BookingMessageType,
    requestOutlookOnlineMeetingUrl,
    subscribeToParentMessages,
} from "../../../../../modules/order/pages/components/bookingMessaging"
import { selectEffectiveUserId } from "../../../../../modules/context/contextSelectors"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { OrderType } from "../../../../../modules/payments/orderTypes"
import { ServiceCenterHomeQuery } from "../../../../../modules/serviceCenter/report/home/serviceCenterHomeApi"
import { ServiceCenterUpcomingMeetingsQuery } from "../../../../../modules/serviceCenter/report/upcomingBookings/upcomingMeetingsApi"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import { OrganizerType } from "../../../../types/OrganizerTypes"
import { isSharedCalendarOrder, toCalendarOrganizerPayload } from "../../../../../modules/order/pages/helpers"
import { getOrganizers } from "../../../../utils/getOrderPerson"
import { CreateUpdateBookingFormValues } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import {
    BookingConfigurationResponse,
    ConfigQuery,
    createBooking,
    CreateUpdateBookingResponse,
    fetchBookingConfiguration,
    updateBooking,
} from "../../createOrderApi"
import {
    CreateOrderDialogMode,
    CreateUpdateBooking,
    CreateUpdateBookingOrganizer,
    ResourceItem,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { useCreateOrderFormConfig } from "../../mainForm/CreateOrderFormContext"
import { OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { bookingFormValuesToDynamicConfigKey, generateConfirmBookingInitialValues } from "../form/helpers"
import type { DynamicMainFormValues } from "../form/formTypes"
import { mergeValuesWithConfig } from "../form/mergeValuesWithConfig"

import type { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import { CreateUpdateBookingDialogWrap } from "./CreateUpdateBookingDialogWrap"
import { buildFinalResources } from "./helpers"
import { useConfirmBookingDialogActions } from "./ResourceBookingConfirmDialogContext"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"
import { useResourceBookingDraftActions, useResourceBookingDraftData } from "./ResourceBookingDraftContext"

const selectBookingConfig = (data: BookingConfigurationResponse) => data?.bookingConfig

type ResourceBookingConfirmDialogProps = Readonly<{
    open: boolean
    user: SelectedMainUserState
    orderId?: number
    initialEventStateId?: number | null
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    orderDetails?: MeetingServiceOrder
    mode?: CreateOrderDialogMode
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    handleOpenSuccessDialog: (
        details: CreateUpdateBookingResponse,
        content: CreateUpdateBooking,
        isBookingUpdate: boolean,
    ) => void
    handleClearResourceIds: () => void
}>

export function ResourceBookingConfirmDialog({
    open,
    user,
    orderId,
    initialEventStateId,
    orderFormStatusRef,
    orderDetails,
    mode,
    setOrderContext,
    handleOpenSuccessDialog,
    handleClearResourceIds,
}: ResourceBookingConfirmDialogProps) {
    const dateTimeFactory = useDateTime()
    const { currentEnv } = useEnvironment()
    const { dialogType, flowConfig, calendarOrganizer } = useCreateOrderFormConfig()
    const { setConfirmBookingDialog } = useConfirmBookingDialogActions()
    const { triggerRefetch } = useRefreshCalendar()
    const { isFullInitOrder, meetingServiceId, resourceBookingId } = orderFormStatusRef.current

    const currentUserId = useAppSelector(selectEffectiveUserId)

    const { values, setFieldValue } = useFormikContext<DynamicMainFormValues>()
    // Source is the draft, not Formik `values.event`: the draft is what the timeline renders, while
    // `values.event` only catches up on Book/Update and some openers never sync it at all.
    // Pinned at open so what is shown and what is submitted are decided once — the inner Formik
    // freezes its own initial values later, when `bookingConfig` resolves.
    const { draftEvent, fullResources: draftFullResources } = useResourceBookingDraftData()
    const { setAndSyncDraftEvent } = useResourceBookingDraftActions()
    const openedBookingRef = useRef({ event: draftEvent, fullResources: draftFullResources })
    const { event, fullResources } = openedBookingRef.current

    const meetingOrganizers = orderDetails ? (getOrganizers(orderDetails) ?? []) : []
    // Outlook create synthesises the order from the add-in session, where organizers have a name but
    // no id — with nothing to match against, "unknown owner" must not read as "somebody else's".
    const hasIdentifiableOrganizer = meetingOrganizers.some((organizer) => Boolean(organizer.id))
    const isLockedForOtherUser =
        Boolean(orderDetails?.meetingDetails?.isPrivate) &&
        hasIdentifiableOrganizer &&
        !meetingOrganizers.some((organizer) => organizer.id === currentUserId)

    const { data: bookingConfig, isInitialLoading } = useApiQuery(fetchBookingConfiguration, {
        queryName: ConfigQuery.BOOKING_CONFIG,
        dependencies: { userId: user.userId },
        select: selectBookingConfig,
        enabled: !!event,
        useErrorBoundary: true,
    })

    const handleDialogClose = useCallback(
        (formValues?: CreateUpdateBookingFormValues) => {
            if (formValues) {
                // The helper keeps start/end from the `event` it is given and filters the list it is
                // given, so both come from the booking this dialog was opened with.
                const {
                    event: nextEvent,
                    fullResourcesList: nextFullResources,
                    ...restFields
                } = bookingFormValuesToDynamicConfigKey(formValues, {
                    ...values,
                    event,
                    fullResourcesList: fullResources,
                })

                Object.entries(restFields).forEach(([key, value]) => {
                    setFieldValue(key, value)
                })

                // Deselecting a resource here has to reach the timeline, and Formik writes no longer
                // travel to the draft on their own.
                const draftEventUpdate: NullableCalendarEvent = nextEvent
                const draftResourcesUpdate: ResourceItem[] = nextFullResources
                setAndSyncDraftEvent(draftEventUpdate, draftResourcesUpdate)
            }

            setConfirmBookingDialog(false)
        },
        [values, event, fullResources, setFieldValue, setAndSyncDraftEvent],
    )

    // Pass the FULL response to handleOpenSuccessDialog — it also reads serviceResponse /
    // resultDetails.busyResourceDates to show the busy-dates dialog on PARTIAL_SUCCESS.
    const { mutateAsync: callCreateBooking } = useApiMutation(createBooking, {
        async onSuccess(params, response, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
            })
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
            })
            orderFormStatusRef.current.resourceBookingId = response.order.id
            setOrderContext((prev) => {
                return { ...prev, orderId: response.order.id }
            })
            handleOpenSuccessDialog(response, params.content, false)
        },
        // onSettled, not onError — a custom onError would replace the backend's error snackbar
        async onSettled(_params, _data, error) {
            if (error) triggerRefetch()
        },
    })

    const { mutateAsync: callUpdateBooking } = useApiMutation(updateBooking, {
        async onSuccess(params, response, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
            })
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
            })
            response.order && handleOpenSuccessDialog(response, params.content, true)
        },
        async onSettled(_params, _data, error) {
            if (error) triggerRefetch()
        },
    })

    if (!open || !event) {
        return null
    }

    const isLoading = isInitialLoading || !bookingConfig

    const mergedValues = bookingConfig
        ? mergeValuesWithConfig({
              existing: values,
              config: bookingConfig,
              user,
              dateTimeFactory,
              dialogType,
              orderDetails,
              mode,
              flowConfig,
          })
        : null

    // One-Touch-Join: rooms booked through an EWS integration get the online meeting URL written into
    // their Exchange event, so AV equipment can offer the meeting on the room display. Only the add-in
    // can read the Outlook body, so it is asked for the URL — once, when this dialog opens, so the
    // answer is already in hand by the time the user books and submission stays synchronous.
    //
    // The per-room EWS flag isn't exposed on the booking-flow resource list, so we ask whenever the
    // flow came from Outlook; the backend applies the URL only where an EWS integration is configured.
    // `undefined` means "no answer (yet)" and omits the field — an add-in without this handshake
    // simply never replies, and booking is unaffected.
    const onlineMeetingUrlRef = useRef<string | null | undefined>(undefined)

    useEffect(() => {
        if (!open || !flowConfig?.calendarAppointmentId) return

        const unsubscribe = subscribeToParentMessages((msg) => {
            if (msg.type !== BookingMessageType.OUTLOOK_ONLINE_MEETING_URL_PROVIDE) return
            // Unreadable body: omit the field rather than clear a URL the order already holds.
            onlineMeetingUrlRef.current = msg.isAvailable === false ? undefined : msg.onlineMeetingUrl
        })

        const parentOrigin = new URLSearchParams(window.location.search).get("parentOrigin") || "*"
        requestOutlookOnlineMeetingUrl(parentOrigin, currentEnv).catch((e) => {
            console.error("Failed to request the Outlook online meeting URL", e)
        })

        return unsubscribe
    }, [open, flowConfig?.calendarAppointmentId, currentEnv])

    const handleSubmit = ({
        meetingDetails,
        resources,
        organizers,
        attendees,
        workgroupId,
        ...rest
    }: CreateUpdateBookingFormValues) => {
        const finalResources = buildFinalResources(meetingDetails.resourceIds, resources)

        // See the meeting-service path for the reasoning; this endpoint differs only in shape
        // (`organizerType` instead of `type`, array at the top level of the body).
        const isOutlookFlow = !!flowConfig
        const orderPeople = { customerId: orderDetails?.customer?.id, organizers: meetingOrganizers }
        const sharedCalendarOrganizer = isSharedCalendarOrder(
            flowConfig?.isSharedCalendar,
            calendarOrganizer,
            orderPeople,
        )
            ? calendarOrganizer
            : undefined

        // Prefetched when the dialog opened (see the effect above); `undefined` while no answer has
        // arrived, which simply omits the field and leaves whatever the order already holds.
        const onlineMeetingUrl = finalResources.length > 0 ? onlineMeetingUrlRef.current : undefined

        const organizersList: CreateUpdateBookingOrganizer[] = []

        if (sharedCalendarOrganizer) {
            // ORGANIZER is taken by the calendar, so the acting user goes to CREATOR and the person
            // picked in the form becomes RESPONSIBLE — the role that field actually represents.
            organizersList.push({ id: user.userId, organizerType: OrganizerType.CREATOR })
        } else {
            // Copied only inside the add-in flow, where the address was just resolved for this meeting.
            // Known gap: re-saving an own-calendar order from Manager drops it.
            organizersList.push({
                id: user.userId,
                organizerType: OrganizerType.ORGANIZER,
                ...(isOutlookFlow && calendarOrganizer?.email ? { email: calendarOrganizer.email } : {}),
            })
        }

        if (organizers?.id) {
            organizersList.push({
                id: organizers.id,
                organizerType: OrganizerType.RESPONSIBLE,
            })
            // A name-only responsible cannot go on a shared-calendar order — the calendar already
            // takes the one id-less organizer the API tolerates. Fall back to the acting user by id
            // so the order always has a RESPONSIBLE; see the meeting-service path.
        } else if (sharedCalendarOrganizer) {
            organizersList.push({
                id: user.userId,
                organizerType: OrganizerType.RESPONSIBLE,
            })
        } else if (organizers?.name && organizers.name.trim()) {
            organizersList.push({
                organizerType: OrganizerType.RESPONSIBLE,
                name: organizers.name.trim(),
            })
        }

        if (sharedCalendarOrganizer) {
            organizersList.push({
                ...toCalendarOrganizerPayload(sharedCalendarOrganizer),
                organizerType: OrganizerType.ORGANIZER,
            })
        }

        const { resourceIds, startDateTime, endDateTime, ...restMeetingDetails } = meetingDetails

        // For Outlook-linked orders, pass the recurrence pattern through so the booking applies to
        // every occurrence of the series.
        const outlookRecurrencePattern = flowConfig?.order?.meetingDetails?.recurrence?.pattern

        const modifyValues = {
            orderType: OrderType.MEETING,
            ...rest,
            ...(isOutlookFlow ? {} : { workgroup: workgroupId ? { id: Number(workgroupId) } : null }),
            organizers: organizersList,
            meetingDetails: {
                ...restMeetingDetails,
                startDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(startDateTime)),
                endDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(endDateTime)),
                attendees,
                ...(flowConfig?.calendarAppointmentId && { calendarAppointmentId: flowConfig.calendarAppointmentId }),
                ...(outlookRecurrencePattern ? { recurrence: { pattern: outlookRecurrencePattern } } : {}),
                // Sent only for the Outlook flow; `null` is meaningful (clears a previously stored URL),
                // so this is an explicit undefined check rather than a truthiness check.
                ...(onlineMeetingUrl !== undefined && { onlineMeetingUrl }),
            },
            bookingDetails: {
                resources: finalResources,
            },
        }

        if (isFullInitOrder || resourceBookingId) {
            let meetingId = initialEventStateId || orderId
            callUpdateBooking({ userId: user.userId, orderId: meetingId ?? 0, content: modifyValues })
        } else {
            callCreateBooking({
                userId: user.userId,
                content: {
                    ...modifyValues,
                    ...(meetingServiceId ? { order: { id: meetingServiceId } } : {}),
                },
            })
        }
    }

    return (
        <CreateUpdateBookingDialogWrap
            open={open}
            onClose={handleDialogClose}
            bookingConfig={bookingConfig}
            event={event}
            initialValues={mergedValues ? generateConfirmBookingInitialValues(mergedValues, initialEventStateId) : null}
            resourcesList={fullResources}
            handleSubmit={handleSubmit}
            handleClearResourceIds={handleClearResourceIds}
            user={user}
            orderId={isFullInitOrder ? orderId : resourceBookingId}
            isLoading={isLoading}
            isLockedForOtherUser={isLockedForOtherUser}
        />
    )
}
